import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, fileSrc } from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Plus, Trash2, FileDown, Send, Presentation, Check, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { CURRENCIES, fmtMoney } from "../../lib/currency";

const lbl = "text-xs uppercase tracking-wider text-[#5C5651]";
const fieldCls = "mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]";

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-[#EBE5E0] text-[#5C5651]" },
  sent: { label: "Sent", cls: "bg-[#A68A6B] text-white" },
  approved: { label: "Approved", cls: "bg-[#506B52] text-white" },
  rejected: { label: "Rejected", cls: "bg-[#9E4747] text-white" },
};

function downloadProposal(id, fallback) {
  return api.get(`/proposals/${id}/download`, { responseType: "blob" }).then((res) => {
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    a.download = m ? m[1] : fallback;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  });
}

function ProposalForm({ projectId, project, schemes, onCreated, onClose }) {
  const [f, setF] = useState({
    title: "",
    client_name: project?.client_name || project?.client_email || "",
    project_reference: project?.name || "",
    date: new Date().toISOString().slice(0, 10),
    intro: "",
    terms: "",
    currency: "CAD",
  });
  const [schemeId, setSchemeId] = useState("");
  const [images, setImages] = useState([]);
  const [fees, setFees] = useState([{ label: "", amount: "" }]);
  const [feeTotalEdit, setFeeTotalEdit] = useState("");
  const [ftEdited, setFtEdited] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const pickScheme = (id) => {
    setSchemeId(id);
    const s = schemes.find((x) => x.id === id);
    setImages(s ? (s.images || []).map((i) => ({ data_url: i.data_url, caption: i.caption || "" })) : []);
    if (s && !f.title) set("title", `${s.name || "Design"} — Proposal`);
  };
  const removeImage = (i) => setImages((p) => p.filter((_, idx) => idx !== i));

  const setFee = (i, k, v) => setFees((p) => p.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addFee = () => setFees((p) => [...p, { label: "", amount: "" }]);
  const removeFee = (i) => setFees((p) => p.filter((_, idx) => idx !== i));

  const computedTotal = useMemo(() => fees.reduce((s, x) => s + (Number(x.amount) || 0), 0), [fees]);
  const feeTotal = ftEdited ? Number(feeTotalEdit) || 0 : computedTotal;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) { toast.error("Add a proposal title"); return; }
    setBusy(true);
    try {
      const payload = {
        ...f,
        scheme_id: schemeId || null,
        images,
        fees: fees.filter((x) => x.label.trim() || x.amount !== "").map((x) => ({ label: x.label.trim(), amount: Number(x.amount) || 0 })),
        fee_total: feeTotal,
      };
      const { data } = await api.post(`/projects/${projectId}/proposals`, payload);
      toast.success("Proposal saved to Proposals");
      onCreated(data);
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4 mt-2 max-h-[72vh] overflow-y-auto pr-1" data-testid="proposal-form">
      <div>
        <Label className={lbl}>Start from a Design Scheme (optional)</Label>
        <Select value={schemeId} onValueChange={pickScheme}>
          <SelectTrigger data-testid="prop-scheme-select" className="mt-1 rounded-none border-[#EBE5E0] focus:ring-0">
            <SelectValue placeholder={schemes.length ? "Select a design scheme…" : "No design schemes yet"} />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            {schemes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || "Untitled scheme"}</SelectItem>)}
          </SelectContent>
        </Select>
        {images.length > 0 && (
          <div className="grid grid-cols-4 gap-2 mt-3" data-testid="prop-scheme-images">
            {images.map((img, i) => (
              <div key={i} className="relative group">
                <img src={fileSrc(img.data_url)} alt={img.caption} className="w-full h-20 object-cover border border-[#EBE5E0]" />
                <button type="button" onClick={() => removeImage(i)}
                  className="absolute top-1 right-1 bg-white/90 text-[#9E4747] p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3 border-t border-[#EBE5E0] pt-4">
        <div className="sm:col-span-2"><Label className={lbl}>Proposal title</Label>
          <Input data-testid="prop-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Living & Dining Redesign" className={fieldCls} /></div>
        <div><Label className={lbl}>Prepared for</Label>
          <Input data-testid="prop-client" value={f.client_name} onChange={(e) => set("client_name", e.target.value)} className={fieldCls} /></div>
        <div><Label className={lbl}>Project reference</Label>
          <Input data-testid="prop-project-ref" value={f.project_reference} onChange={(e) => set("project_reference", e.target.value)} className={fieldCls} /></div>
        <div><Label className={lbl}>Date</Label>
          <Input data-testid="prop-date" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className={fieldCls} /></div>
        <div><Label className={lbl}>Currency</Label>
          <Select value={f.currency} onValueChange={(v) => set("currency", v)}>
            <SelectTrigger data-testid="prop-currency" className="mt-1 rounded-none border-[#EBE5E0] focus:ring-0"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="border-t border-[#EBE5E0] pt-4">
        <Label className={lbl}>Overview / scope</Label>
        <Textarea data-testid="prop-intro" value={f.intro} onChange={(e) => set("intro", e.target.value)} rows={4}
          placeholder="Describe the vision, scope of work and what's included…" className={fieldCls} />
      </div>

      <div className="border-t border-[#EBE5E0] pt-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#A68A6B]">Investment</p>
          <button type="button" data-testid="prop-add-fee" onClick={addFee}
            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-[#A68A6B] hover:text-[#8C7154]"><Plus size={13} /> Add line</button>
        </div>
        <div className="space-y-2">
          {fees.map((x, i) => (
            <div key={i} className="grid grid-cols-[1fr_130px_28px] gap-2 items-center" data-testid={`prop-fee-row-${i}`}>
              <Input data-testid={`prop-fee-label-${i}`} value={x.label} onChange={(e) => setFee(i, "label", e.target.value)} placeholder="e.g. Design fee" className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
              <Input data-testid={`prop-fee-amount-${i}`} type="number" step="0.01" value={x.amount} onChange={(e) => setFee(i, "amount", e.target.value)} placeholder="0.00" className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
              <button type="button" onClick={() => removeFee(i)} className="text-[#5C5651] hover:text-[#9E4747]"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
        <div className="flex justify-between items-center gap-2 pt-3 mt-2 border-t border-[#EBE5E0] max-w-xs ml-auto">
          <span className="text-[#2B2622] font-medium uppercase tracking-wider text-xs">Total</span>
          <Input data-testid="prop-fee-total" type="number" step="0.01"
            value={ftEdited ? feeTotalEdit : computedTotal.toFixed(2)}
            onChange={(e) => { setFtEdited(true); setFeeTotalEdit(e.target.value); }}
            className="rounded-none border-[#A68A6B] focus-visible:ring-0 h-9 w-32 text-right font-medium" />
        </div>
      </div>

      <div className="border-t border-[#EBE5E0] pt-4">
        <Label className={lbl}>Terms</Label>
        <Textarea data-testid="prop-terms" value={f.terms} onChange={(e) => set("terms", e.target.value)} rows={3}
          placeholder="Payment schedule, validity, deposit…" className={fieldCls} />
      </div>

      <Button type="submit" disabled={busy} data-testid="prop-create-submit"
        className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
        {busy ? "Saving…" : "Save Proposal"}
      </Button>
    </form>
  );
}

export default function ProposalsTab({ projectId, project }) {
  const { user, previewClient } = useAuth();
  const isDesigner = user?.role === "designer" && !previewClient;
  const [proposals, setProposals] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [emailFor, setEmailFor] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/projects/${projectId}/proposals`).then((r) => setProposals(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (isDesigner) {
      api.get(`/projects/${projectId}/moodboards`).then((r) => setSchemes((r.data || []).filter((b) => b.kind === "scheme"))).catch(() => {});
    }
  }, [projectId]);

  const setStatus = async (p, status, comment) => {
    try {
      const { data } = await api.put(`/proposals/${p.id}/status`, { status, comment: comment || "" });
      setProposals((arr) => arr.map((x) => (x.id === p.id ? data : x)));
      toast.success(`Proposal ${status}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const sendToClient = async (p) => {
    try {
      const { data } = await api.post(`/proposals/${p.id}/send`);
      setProposals((arr) => arr.map((x) => (x.id === p.id ? data : x)));
      toast.success("Proposal shared with client");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/proposals/${id}`); setProposals((p) => p.filter((x) => x.id !== id)); };

  const download = async (p) => {
    setDownloading(p.id);
    try { await downloadProposal(p.id, `${p.title || "proposal"}.pdf`); }
    catch { toast.error("Could not download proposal"); }
    finally { setDownloading(null); }
  };

  const openEmail = (p) => { setEmailFor(p); setEmailTo(project?.client_email || ""); setEmailMsg(""); };
  const sendEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(`/proposals/${emailFor.id}/email`, { to: emailTo, message: emailMsg });
      toast.success(`Proposal sent to ${data.to}`);
      setEmailFor(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="proposals-tab">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A68A6B] mb-1">Presentation</p>
          <h3 className="font-serif text-3xl text-[#2B2622]">Proposals</h3>
        </div>
        {isDesigner && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-proposal-btn"
                className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5 px-5">
                <Plus size={15} className="mr-2" /> Create Proposal
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-[#EBE5E0] bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#2B2622]">New proposal</DialogTitle>
                <DialogDescription className="text-sm text-[#5C5651]">Turn a design scheme into a client-ready presentation. Your studio branding is added automatically.</DialogDescription>
              </DialogHeader>
              <ProposalForm projectId={projectId} project={project} schemes={schemes}
                onClose={() => setCreateOpen(false)} onCreated={(data) => setProposals((p) => [data, ...p])} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {proposals.length === 0 ? (
        <div className="border border-dashed border-[#D9CBBF] bg-white p-12 text-center text-[#5C5651]">
          <Presentation size={28} strokeWidth={1.25} className="mx-auto mb-3 text-[#C9BBAE]" />
          {isDesigner ? "No proposals yet. Create one from a design scheme." : "No proposals have been shared with you yet."}
        </div>
      ) : (
        <div className="space-y-px bg-[#EBE5E0] border border-[#EBE5E0]">
          {proposals.map((p) => {
            const meta = STATUS_META[p.status || "draft"];
            return (
              <div key={p.id} className="bg-white p-5" data-testid={`proposal-card-${p.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0">
                    <FileText size={20} strokeWidth={1.5} className="text-[#A68A6B] shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-base text-[#2B2622] font-medium">{p.title || "Design Proposal"}</p>
                      <p className="text-[11px] uppercase tracking-wider text-[#A68A6B] mt-0.5">
                        {p.fee_total ? fmtMoney(p.fee_total, p.currency) : "—"}
                        {p.client_name ? ` · ${p.client_name}` : ""}
                      </p>
                      {p.response_comment && <p className="text-[11px] text-[#9b938b] mt-1 italic">“{p.response_comment}”</p>}
                      {p.sent_to && <p className="text-[11px] text-[#9b938b] mt-1">Sent to {p.sent_to}</p>}
                    </div>
                  </div>
                  <span data-testid={`proposal-status-${p.id}`} className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${meta.cls}`}>{meta.label}</span>
                </div>

                <div className="mt-4 pt-3 border-t border-[#F2EFEB] flex flex-wrap items-center gap-2">
                  <button data-testid={`download-proposal-${p.id}`} onClick={() => download(p)} disabled={downloading === p.id}
                    className="inline-flex items-center gap-1.5 border border-[#A68A6B] text-[#A68A6B] hover:bg-[#A68A6B] hover:text-white transition-colors px-3 py-2 text-[11px] uppercase tracking-wider disabled:opacity-60">
                    <FileDown size={14} strokeWidth={1.5} /> {downloading === p.id ? "Preparing…" : "PDF"}
                  </button>

                  {isDesigner && (
                    <>
                      {(p.status || "draft") === "draft" && (
                        <button data-testid={`send-proposal-${p.id}`} onClick={() => sendToClient(p)}
                          className="inline-flex items-center gap-1.5 border border-[#A68A6B] text-[#A68A6B] hover:bg-[#A68A6B] hover:text-white transition-colors px-3 py-2 text-[11px] uppercase tracking-wider">
                          <Send size={13} strokeWidth={1.5} /> Send to client
                        </button>
                      )}
                      <button data-testid={`email-proposal-${p.id}`} onClick={() => openEmail(p)}
                        className="inline-flex items-center gap-1.5 border border-[#A68A6B] text-[#A68A6B] hover:bg-[#A68A6B] hover:text-white transition-colors px-3 py-2 text-[11px] uppercase tracking-wider">
                        <Send size={13} strokeWidth={1.5} /> Email
                      </button>
                      {p.status !== "approved" && (
                        <button data-testid={`approve-proposal-${p.id}`} onClick={() => setStatus(p, "approved")}
                          className="inline-flex items-center gap-1.5 border border-[#506B52] text-[#3f5641] hover:bg-[#506B52] hover:text-white transition-colors px-3 py-2 text-[11px] uppercase tracking-wider">
                          <Check size={13} strokeWidth={2} /> Mark approved
                        </button>
                      )}
                      <button data-testid={`delete-proposal-${p.id}`} onClick={() => remove(p.id)}
                        className="ml-auto text-[#5C5651] hover:text-[#9E4747]"><Trash2 size={15} strokeWidth={1.5} /></button>
                    </>
                  )}

                  {!isDesigner && (p.status || "draft") === "sent" && (
                    <>
                      <button data-testid={`approve-proposal-${p.id}`} onClick={() => setStatus(p, "approved")}
                        className="inline-flex items-center gap-1.5 bg-[#506B52] text-white hover:bg-[#3f5641] transition-colors px-3 py-2 text-[11px] uppercase tracking-wider">
                        <Check size={13} strokeWidth={2} /> Approve
                      </button>
                      <button data-testid={`reject-proposal-${p.id}`} onClick={() => setStatus(p, "rejected")}
                        className="inline-flex items-center gap-1.5 border border-[#9E4747] text-[#9E4747] hover:bg-[#9E4747] hover:text-white transition-colors px-3 py-2 text-[11px] uppercase tracking-wider">
                        <X size={13} strokeWidth={2} /> Decline
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!emailFor} onOpenChange={(o) => { if (!o) setEmailFor(null); }}>
        <DialogContent className="rounded-none border-[#EBE5E0] bg-white" data-testid="proposal-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#2B2622]">Send proposal</DialogTitle>
            <DialogDescription className="text-sm text-[#5C5651]">The proposal PDF is emailed and the client can review it in WorkRoom.</DialogDescription>
          </DialogHeader>
          <form onSubmit={sendEmail} className="space-y-4 mt-2">
            <div>
              <Label className={lbl}>Recipient email</Label>
              <Input data-testid="prop-email-to" type="email" required value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                placeholder="client@email.com" className={fieldCls} />
            </div>
            <div>
              <Label className={lbl}>Message (optional)</Label>
              <Textarea data-testid="prop-email-message" value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} className={fieldCls} />
            </div>
            <Button type="submit" disabled={sending} data-testid="prop-email-submit"
              className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
              {sending ? "Sending…" : "Send Proposal"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
